import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import SimpleBar from 'simplebar-react';
import BtnOutlineGradient from 'src/components/common/button/BtnOutlineGradient';
import DetailAnswer from 'src/components/patients/answers/DetailAnswer';
import DetailHistoryList from 'src/components/patients/answers/DetailHistoryList';
import DialogForm from 'src/components/patients/answers/dialog/DialogForm';
import { getAPI } from 'src/utils/api';
import { getToken } from 'src/utils/auth';
import DialogQRCode from 'src/components/patients/answers/dialog/DialogQRCode';
import { Questionaire } from 'src/common/keyText';
import SnackbarNotify from 'src/components/common/SnackbarNotify';
import { useStore } from 'src/context/StoreContext';

const PatientAnswers = () => {
  const navigate = useNavigate();
  const storeContext = useStore();
  const [searchParams] = useSearchParams();
  const patientid = searchParams.get('id');
  const [dataAnswers, setDataAnswers] = useState<any>(null);
  const [logAnswer, setLogAnswer] = useState<any>(null);
  const [openForm, setOpenForm] = useState(false);
  const [openQRCode, setOpenQRCode] = useState(false);
  const [answerToken, setAnswerToken] = useState<string | null>(null);
  const [notify, setNotify] = useState<any>({
    show: false,
    message: '',
    type: 'error',
  });

  const getAnswer = () => {
    const token = getToken();
    if (!token) {
      throw new Error('Failed to get access token');
    }
    getAPI(`/staff/answers?patient_id=${patientid}&token=${token}`)
      .then((res) => {
        if (res.code === 200) {
          setDataAnswers(res.data);
          setLogAnswer(res.data ? res.data[0] : null);
        }
      })
      .finally(() => {});
  };

  useEffect(() => {
    if (!patientid) {
      navigate(`/patients`);
    }
    getAnswer();
    // eslint-disable-next-line
  }, [patientid]);

  const getAnswerToken = () => {
    const token = getToken();
    if (!token) {
      throw new Error('Failed to get access token');
    }
    return getAPI(`/staff/answers/token?patientid=${patientid}&token=${token}`)
      .then((res) => {
        if (res.code === 200) {
          return res.data?.token;
        } else {
          throw new Error('QRコードデータの取得に失敗しました');
        }
      });
  };

  const handleUpdateClick = () => {
    setOpenForm(true);
  };
  const handleRowClick = (log: any) => {
    setLogAnswer(log);
  };

  const handleCloseDialogForm = () => {
    setOpenForm(false);
  };

  const handleSubmitForm = () => {
    getAnswer();
    setOpenForm(false);
    setNotify({
      show: true,
      message: Questionaire.SaveSuccess,
      type: 'success',
    });
  };

  const handleError = (msg: string) => {
    setNotify({
      show: true,
      message: msg,
      type: 'error',
    });
  }

  const handleShowQR = async () => {
    try {
      const token = await getAnswerToken();
      setAnswerToken(token);
      setOpenQRCode(true);
    } catch (error: any) {
      setNotify({
        show: true,
        message: error.message || 'QRコードの取得に失敗しました',
        type: 'error',
      });
    }
  };

  const handleCloseQR = () => {
    setAnswerToken(null);
    setOpenQRCode(false);
  };

  const handleBackClick = () => {
    navigate(`/patient/view?id=${patientid}`);
  }

  return (
    <Stack>
      
      {notify.show && (
        <SnackbarNotify
          content={notify.message}
          show={true}
          type={notify.type}
          timeDuration={10000}
          handleCloseAlert={() =>
            setNotify({
              show: false,
              message: '',
              type: 'error',
            })
          }
        />
      )}
      <Box>
        <BtnOutlineGradient
          text='QRコードを表示'
          handleButtonClick={handleShowQR}
          width='124px'
        />
        <Typography fontSize={'12px'}>※QRコードを読み取ると、患者様が入力できる画面を表示します</Typography>
      </Box>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={'20px'}>
        <DetailAnswer
          answer={logAnswer}
          patientid={patientid}
          handleUpdateClick={handleUpdateClick}
        />
        <Stack flex={1}>
          <Typography
            fontSize={'20px'}
            fontWeight={500}
            color={'primary.dark'}
            my={'8px'}
            height={'40px'}
            lineHeight={'40px'}
          >
            変更履歴
          </Typography>
          <Stack
            direction={'column'}
            spacing={'20px'}
            width={'100%'}
            sx={{
              '& .simplebar-track.simplebar-vertical .simplebar-scrollbar:before':
                { backgroundColor: '#CCD1D9' },
            }}
          >
            <SimpleBar style={{ maxHeight: 'calc(100vh - 240px)', width: '100%' }}>
              <DetailHistoryList
                dataAnswers={dataAnswers}
                handleRowClick={handleRowClick}
              />
            </SimpleBar>
            {!storeContext.inIframe && <BtnOutlineGradient text='カルテ' handleButtonClick={handleBackClick} />}
          </Stack>
        </Stack>
      </Stack>
      <DialogForm
        open={openForm}
        handleClose={handleCloseDialogForm}
        submission_id={logAnswer?.submission_id}
        handleConfirm={handleSubmitForm}
        handleError={handleError}
        patientid={patientid}
      />
      <DialogQRCode
        open={openQRCode}
        answerToken={answerToken}
        handleShow={handleShowQR}
        handleClose={handleCloseQR}
      />
    </Stack>
  );
};

export default PatientAnswers;
